import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/seo/SEO";
import { Calendar, ArrowRight } from "lucide-react";

function formatDate(iso, locale = "es-ES") {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(locale, {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function Blog() {
    const { t } = useTranslation();
    const dateLocale = t("blog.locale");
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/blog", { params: { publicado: true } })
            .then((r) => setPosts(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <article className="pt-32 pb-24 paper-grain">
            <SEO
                title={t("blog.seo.title")}
                description={t("blog.seo.description")}
                keywords="blog restaurante Ocaña, noticias Tenería, eventos Ocaña, gastronomía manchega, sugerencias temporada Ocaña"
                path="/blog"
                breadcrumbs={[
                    { name: t("nav.home"), item: "/" },
                    { name: t("nav.blog"), item: "/blog" },
                ]}
            />

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-6 text-center mb-24">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    className="roman-numeral"
                >
                    — {t("blog.kicker")} —
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="font-display font-light text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] text-ivory mt-8"
                >
                    {t("blog.title1")} <span className="italic text-gold">{t("blog.title2")}</span>
                </motion.h1>
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.8, duration: 1 }}
                    className="line-gold mx-auto my-10 origin-left"
                />
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 1.2 }}
                    className="font-body text-lg text-stone max-w-2xl mx-auto leading-relaxed"
                >
                    {t("blog.subtitle")}
                </motion.p>
            </section>

            {/* Posts */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-12">
                {loading ? (
                    <p className="text-stone italic font-editorial text-center">
                        {t("blog.loading")}
                    </p>
                ) : posts.length === 0 ? (
                    <p className="text-stone italic font-editorial text-center py-16">
                        {t("blog.empty")}
                    </p>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gold/15">
                        {posts.map((p, i) => (
                            <motion.div
                                key={p.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-30px" }}
                                transition={{ duration: 0.8, delay: Math.min(i * 0.08, 0.4) }}
                                className="bg-bg group"
                            >
                                <Link
                                    to={`/blog/${p.slug}`}
                                    className="block p-8 md:p-10 hover:bg-bg-warm transition-colors duration-700 h-full"
                                    data-cursor="hover"
                                >
                                    {p.imagen_url ? (
                                        <div className="aspect-[16/10] overflow-hidden mb-6 border border-gold/15 bg-bg-alt flex items-center justify-center">
                                            <img
                                                src={p.imagen_url}
                                                alt={p.titulo}
                                                className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-1000 group-hover:scale-105"
                                            />
                                        </div>
                                    ) : (
                                        <div className="aspect-[16/10] placeholder-art text-gold mb-6 flex items-center justify-center">
                                            <span className="font-display italic text-gold/40">
                                                Tenería
                                            </span>
                                        </div>
                                    )}
                                    <p className="font-editorial italic text-xs tracking-[0.2em] uppercase text-gold/70 flex items-center gap-2">
                                        <Calendar size={12} />
                                        {formatDate(p.fecha_pub || p.creado_en, dateLocale)}
                                    </p>
                                    <h2 className="font-display text-2xl md:text-3xl text-ivory font-light mt-4 group-hover:text-gold transition-colors duration-500">
                                        {p.titulo}
                                    </h2>
                                    {p.extracto && (
                                        <p className="font-body text-sm text-stone mt-3 leading-relaxed line-clamp-3">
                                            {p.extracto}
                                        </p>
                                    )}
                                    <p className="inline-flex items-center gap-2 mt-6 text-gold font-editorial italic text-sm link-underline">
                                        {t("blog.readMore")} <ArrowRight size={14} />
                                    </p>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}
            </section>
        </article>
    );
}
